import type { CmsSymptom } from "@/lib/cms"

interface PackagedDoc {
  id: string
  slug: string
  packages: string[]
}

/** Index a biomarker/disease list by BOTH id and slug → its package ids, so the
 *  lookup works whether a keyRelation's `item.value` is a raw id (depth-0 list
 *  payload) or a populated `{ slug }` object (depth≥2). */
function keyByIdAndSlug(docs: PackagedDoc[]): Map<string, string[]> {
  const map = new Map<string, string[]>()
  for (const d of docs) {
    map.set(String(d.id), d.packages)
    map.set(d.slug, d.packages)
  }
  return map
}

/**
 * Map each symptom slug → the set of test/package ids that cover it, derived
 * from its curated related biomarkers/diseases' package memberships. Mirrors
 * the per-symptom "covered in check-ups" derivation on the detail page, but for
 * the whole list so the overview can filter symptoms by test like the
 * biomarker/disease pages filter by package.
 */
export function buildSymptomPackages(
  symptoms: CmsSymptom[],
  biomarkers: PackagedDoc[],
  diseases: PackagedDoc[]
): Record<string, string[]> {
  const bio = keyByIdAndSlug(biomarkers)
  const dis = keyByIdAndSlug(diseases)
  const out: Record<string, string[]> = {}
  for (const s of symptoms) {
    const pkgs = new Set<string>()
    for (const rel of s.keyRelations ?? []) {
      const item = rel.item
      if (!item) continue
      const v = item.value
      const key =
        typeof v === "object" && v !== null ? (v.slug ?? "") : String(v)
      if (!key) continue
      const map = item.relationTo === "biomarkers" ? bio : dis
      for (const p of map.get(key) ?? []) pkgs.add(p)
    }
    out[s.slug] = Array.from(pkgs)
  }
  return out
}
